import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// A grid (not a single-column stack) so cards genuinely sit side by side for comparison on
// wider screens, wrapping to one column on mobile via auto-fill/minmax.
export const compareGrid = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', alignItems: 'start',
};

// The full-screen comparison overlay first built for School/Union Finder's saved list
// (SavedResourcesPanel): fixed inset:0, body scroll locked, sticky header with an amber eyebrow,
// a Bebas title, an optional "Clear all" and a round × close. Every comparison view in the app
// renders inside this, so they all look and behave the same way.
export default function CompareOverlay({ eyebrow, title, onClose, onClear, clearLabel, closeLabel, children }) {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, backgroundColor: NAVY, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', backgroundColor: NAVY, borderBottom: `2px solid ${AMBER}`, gap: '10px',
      }}>
        {/* Shrinks and wraps its own text, so a long eyebrow never pushes × onto its own line. */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: '0.68rem', fontWeight: 700, color: AMBER, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '3px' }}>
            {eyebrow}
          </p>
          <h1 style={{ fontFamily: FONT_HEAD, fontSize: '1.5rem', color: WHITE, letterSpacing: '0.03em' }}>
            {title}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          {onClear && (
            <button
              onClick={onClear}
              style={{
                fontSize: '0.78rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)',
                background: 'none', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px',
                padding: '8px 12px', cursor: 'pointer', fontFamily: FONT_BODY, whiteSpace: 'nowrap',
              }}
            >
              {clearLabel}
            </button>
          )}
          <button
            onClick={onClose}
            aria-label={closeLabel}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
              backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
              color: WHITE, fontSize: '18px', cursor: 'pointer', fontFamily: FONT_BODY,
            }}
          >
            ×
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px', paddingBottom: '60px' }}>
        {children}
      </div>
    </div>
  );
}

// The ☆/★ toggle from the Finder cards, used here to add/remove an item from a comparison. It
// carries a visible "Compare" label (unlike the Finder's bare star) so it can't be mistaken for
// the employer side's existing "★ Shortlisted" badge.
export function CompareToggle({ selected, onToggle, addLabel, removeLabel }) {
  const { t } = useTranslation();
  const color = selected ? AMBER : 'rgba(255,255,255,0.5)';
  return (
    <button
      onClick={e => { e.stopPropagation(); onToggle(); }}
      aria-label={selected ? removeLabel : addLabel}
      aria-pressed={selected}
      title={selected ? removeLabel : addLabel}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0,
        background: 'none', border: 'none', padding: '2px', cursor: 'pointer', color,
        fontFamily: FONT_BODY, whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: '1.15rem', lineHeight: 1 }}>{selected ? '★' : '☆'}</span>
      <span style={{ fontSize: '0.72rem', fontWeight: 700 }}>{t('compare.toggleLabel')}</span>
    </button>
  );
}

// The Finder's "★ Saved (N)" entry point: always visible near the top of the list, amber once
// anything is selected.
export function CompareEntryButton({ count, label, onClick }) {
  const active = count > 0;
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        backgroundColor: active ? 'rgba(245,166,35,0.12)' : NAVY_MID,
        color: active ? AMBER : WHITE,
        border: `1px solid ${active ? 'rgba(245,166,35,0.4)' : 'rgba(255,255,255,0.12)'}`,
        borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', fontWeight: 700,
        cursor: 'pointer', fontFamily: FONT_BODY,
      }}
    >
      <span>★</span>
      <span>{label}{active ? ` (${count})` : ''}</span>
    </button>
  );
}
